"use client";

import { clsx } from "@trydig/ui";
import { useEmblaCarousel } from "@trydig/ui/embla";
import type { EmblaCarouselType, EmblaOptionsType } from "embla-carousel";
import { WheelGesturesPlugin } from "embla-carousel-wheel-gestures";
import {
	Children,
	type RefObject,
	useEffect,
	useImperativeHandle,
	useRef,
	useState,
} from "react";
import { Arrow } from "@/components/icons/Arrow";
import { Chevron, SkinnyChevron } from "@/components/icons/Chevron";
import styles from "./Carousel.module.css";

export type EmblaOptionsWithScroll = Omit<
	EmblaOptionsType,
	"slidesToScroll"
> & {
	slidesToScroll: number;
	hide?: boolean;
	type: "chevron" | "controls";
};

export interface CarouselProps {
	children: React.ReactNode;
	className?: string;
	containerClassName?: string;
	wrapperClassName?: string;
	ref?: RefObject<EmblaCarouselType | undefined>;
	options?: EmblaOptionsWithScroll;
}

const plugins = [WheelGesturesPlugin()];

export const Carousel = ({
	children,
	className,
	containerClassName,
	wrapperClassName,
	ref: refProp,
	options,
}: CarouselProps) => {
	const [emblaRef, emblaAPI] = useEmblaCarousel(options, plugins);

	const [currentSlide, setCurrentSlide] = useState(0);
	const [isPrevDisabled, setIsPrevDisabled] = useState(true);
	const [isNextDisabled, setIsNextDisabled] = useState(false);

	const items = Children.toArray(children);
	const totalSlides = Math.ceil(items.length / (options?.slidesToScroll ?? 1));

	useImperativeHandle(refProp, () => emblaAPI.current);

	const emblaAPICurrent = useRef<EmblaCarouselType | null>(null);

	useEffect(() => {
		if (!emblaAPI.current) return;

		emblaAPICurrent.current = emblaAPI.current;

		const updateButtonStates = () => {
			setCurrentSlide(emblaAPICurrent.current?.selectedScrollSnap() || 0);
			setIsPrevDisabled(!emblaAPICurrent.current?.canScrollPrev());
			setIsNextDisabled(!emblaAPICurrent.current?.canScrollNext());
		};

		emblaAPICurrent.current.on("select", updateButtonStates);
		emblaAPICurrent.current.on("init", updateButtonStates);
		updateButtonStates();

		return () => {
			if (emblaAPICurrent.current) {
				emblaAPICurrent.current.off("select", updateButtonStates);
				emblaAPICurrent.current.off("init", updateButtonStates);
			}
		};
	}, [emblaAPI.current]);

	const scrollPrev = () => {
		if (emblaAPICurrent.current?.canScrollPrev()) {
			emblaAPICurrent.current.scrollTo(
				emblaAPICurrent.current.selectedScrollSnap() - 1,
			);
		}
	};

	const scrollNext = () => {
		if (emblaAPICurrent.current?.canScrollNext()) {
			emblaAPICurrent.current.scrollTo(
				emblaAPICurrent.current.selectedScrollSnap() + 1,
			);
		}
	};

	return (
		<div
			className={clsx(
				styles.root,
				options?.type === "chevron" && styles.displayChevronOnHover,
				wrapperClassName,
			)}
		>
			<div className={clsx(styles.sliderRoot, className)} ref={emblaRef}>
				<div className={clsx(styles.container, containerClassName)}>
					{children}
				</div>
				{options && !options?.hide && (
					<>
						{options.type === "controls" && (
							<div className={styles.controls}>
								<button
									className={clsx(styles.controlButton, styles.prevButton)}
									onClick={scrollPrev}
									disabled={isPrevDisabled}
								>
									<Chevron
										style={{
											transform: "rotate(180deg)",
										}}
									/>
								</button>
								<span>
									{currentSlide + 1} / {totalSlides}
								</span>
								<button
									className={clsx(styles.controlButton, styles.nextButton)}
									onClick={scrollNext}
									disabled={isNextDisabled}
								>
									<Chevron />
								</button>
							</div>
						)}
						{options.type === "chevron" && (
							<div className={styles.chevronButtons}>
								<button
									className={clsx(styles.chevronButton, styles.nextButton)}
									onClick={scrollNext}
									disabled={isNextDisabled}
								>
									<SkinnyChevron data-initial-icon />
									<Arrow data-hover-icon />
								</button>
								<button
									className={clsx(styles.chevronButton, styles.prevButton)}
									onClick={scrollPrev}
									disabled={isPrevDisabled}
								>
									<SkinnyChevron data-initial-icon />
									<Arrow data-hover-icon />
								</button>
							</div>
						)}
					</>
				)}
			</div>
		</div>
	);
};
